// The editor screen's markup and styles. Every id carries the `ed-` prefix and every rule is scoped under
// .map-editor: the screen lives inside the game page, next to the game's own #stage/#view.
export const EDITOR_CSS = `
  .map-editor {
    --panel: #261e17; --panel-2: #2f251c; --line: #43362a; --ink: #efe5cf; --muted: #a8977c; --bad: #ff6a55; --ok: #8fc27a;
    position: fixed; inset: 0; z-index: 45; background: var(--ground); color: var(--ink); font: 14px/1.35 var(--body);
    display: grid; grid-template-columns: 176px 1fr 300px; grid-template-rows: 1fr 28px; overflow: hidden;
  }
  .map-editor[hidden] { display: none; }
  .map-editor *, .map-editor *::before, .map-editor *::after { box-sizing: border-box; }
  .map-editor button, .map-editor input, .map-editor select { font: inherit; color: inherit; }
  .map-editor button { background: var(--panel-2); border: 1px solid var(--line); border-radius: 3px; padding: 4px 8px; cursor: pointer; }
  .map-editor button:hover, .map-editor button:focus-visible { border-color: var(--gold); }
  .map-editor button:disabled { opacity: .45; cursor: default; border-color: var(--line); }
  .map-editor button.primary { background: #5a3d22; border-color: var(--gold); }
  .map-editor input, .map-editor select { background: #17120e; border: 1px solid var(--line); border-radius: 3px; padding: 3px 6px; min-width: 0; }
  .map-editor input[type=checkbox] { accent-color: var(--gold); }
  .map-editor h2 { font: 700 13px var(--display); letter-spacing: .12em; text-transform: uppercase; color: var(--gold); margin: 14px 0 6px; }
  .map-editor h2:first-child { margin-top: 0; }

  #ed-palette h2 { letter-spacing: .06em; }
  #ed-palette { grid-row: 1 / 3; background: var(--panel); border-right: 1px solid var(--line); padding: 10px 8px; overflow-y: auto; }
  #ed-palette .group { display: grid; gap: 3px; }
  #ed-palette button { text-align: left; display: flex; justify-content: space-between; gap: 6px; padding: 3px 7px; }
  #ed-palette button kbd { color: var(--muted); font: 11px var(--mono); }
  #ed-palette button.on { background: #5a3d22; border-color: var(--gold); }

  #ed-stage { position: relative; overflow: hidden; background: #efe3c6; }
  #ed-view { display: block; width: 100%; height: 100%; }
  #ed-stage[data-tool="select"] #ed-view { cursor: default; }
  #ed-stage:not([data-tool="select"]) #ed-view { cursor: crosshair; }
  #ed-stage.panning #ed-view { cursor: grabbing; }
  #ed-hint { position: absolute; left: 10px; top: 10px; background: rgba(29,23,18,.85); padding: 4px 9px; border-radius: 3px; font-size: 13px; pointer-events: none; max-width: 70%; }
  #ed-toast { position: absolute; left: 50%; bottom: 14px; transform: translateX(-50%); background: rgba(29,23,18,.92); border: 1px solid var(--gold);
           padding: 6px 12px; border-radius: 3px; pointer-events: none; max-width: 90%; }
  #ed-draftBar { position: absolute; left: 50%; bottom: 54px; transform: translateX(-50%); background: rgba(29,23,18,.92); border: 1px solid var(--gold);
              padding: 6px 10px; border-radius: 3px; display: flex; align-items: center; gap: 10px; font-size: 13px; white-space: nowrap; }
  #ed-draftBar button { padding: 3px 10px; }
  #ed-help { position: absolute; right: 12px; top: 12px; width: min(440px, 90%); max-height: calc(100% - 24px); overflow-y: auto; background: rgba(29,23,18,.95);
          border: 1px solid var(--line); border-radius: 4px; padding: 12px 14px; font-size: 13px; }
  #ed-help table { border-collapse: collapse; width: 100%; }
  #ed-help td { padding: 2px 4px; vertical-align: top; }
  #ed-help td:first-child { font: 12px var(--mono); color: var(--gold); white-space: nowrap; padding-right: 10px; }

  #ed-side { grid-row: 1 / 3; grid-column: 3; background: var(--panel); border-left: 1px solid var(--line); padding: 10px 12px; overflow-y: auto; }
  .map-editor .row { display: grid; grid-template-columns: 92px 1fr; gap: 6px; align-items: center; margin: 4px 0; }
  .map-editor .row label { color: var(--muted); font-size: 13px; }
  .map-editor .pair { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
  .map-editor .buttons { display: flex; flex-wrap: wrap; gap: 5px; margin: 6px 0; }
  .map-editor .note { color: var(--muted); font-size: 12.5px; margin: 2px 0 6px; }
  .map-editor .checks { display: grid; gap: 3px; }
  .map-editor .checks label { display: flex; gap: 6px; align-items: center; }
  #ed-docName { font: 700 15px var(--display); overflow-wrap: anywhere; }
  #ed-inspector .kind { font: 700 15px var(--display); }
  #ed-inspector .kind small { font: 12px var(--body); color: var(--muted); margin-left: 6px; }
  #ed-issues { list-style: none; margin: 0; padding: 0; display: grid; gap: 3px; font-size: 12.5px; }
  #ed-issues li { padding: 3px 6px; background: #3a1f19; border-left: 3px solid var(--bad); cursor: pointer; overflow-wrap: anywhere; }
  #ed-issues li b { font: 11.5px var(--mono); color: #ffb3a6; }
  #ed-issues li.none { background: #1f2a1a; border-color: var(--ok); cursor: default; }
  #ed-issues li.head { background: none; border: 0; padding: 0; color: var(--bad); cursor: default; }
  #ed-status { grid-column: 2; background: var(--panel); border-top: 1px solid var(--line); font: 12px var(--mono); color: var(--muted);
            display: flex; gap: 18px; align-items: center; padding: 0 10px; white-space: nowrap; overflow: hidden; }
  #ed-status .dirty { color: var(--gold); }
`;

/* Texts are filled from the dictionary (data-i18n-*, i18n/dom.ts applyI18nAttrs) — again after a language
   switch, since the editor outlives it. */
export const EDITOR_HTML = `
<aside id="ed-palette"></aside>
<main id="ed-stage" data-tool="select">
  <canvas id="ed-view"></canvas>
  <div id="ed-hint"></div>
  <div id="ed-toast" role="status" hidden></div>
  <div id="ed-draftBar" hidden>
    <span data-i18n-html="editor.draftBar"></span>
    <button type="button" id="ed-draftDone" data-i18n-html="editor.done"></button>
    <button type="button" id="ed-draftCancel" data-i18n-html="editor.cancel"></button>
  </div>
  <div id="ed-help" hidden data-i18n-html="editor.help"></div>
</main>
<aside id="ed-side">
  <div class="buttons"><button type="button" id="ed-back" data-i18n-html="editor.back"></button><button type="button" id="ed-helpBtn" data-i18n-html="editor.helpBtn"></button></div>
  <h2 data-i18n-html="editor.map"></h2>
  <div id="ed-docName"></div>
  <div class="row"><label for="ed-mapName" data-i18n-html="editor.name"></label><input id="ed-mapName"></div>
  <div class="row"><label data-i18n-html="editor.size"></label><div class="pair"><input id="ed-width" type="number" step="0.5"><input id="ed-height" type="number" step="0.5"></div></div>
  <div class="row"><label for="ed-pointRadius" data-i18n-html="editor.pointRadius"></label><input id="ed-pointRadius" type="number" min="1" step="0.1"></div>
  <div class="buttons">
    <button type="button" id="ed-save" class="primary" data-i18n-html="editor.save"></button>
    <button type="button" id="ed-saveAs" data-i18n-html="editor.saveAs"></button>
    <button type="button" id="ed-play" class="primary" data-i18n-html="editor.play"></button>
    <button type="button" id="ed-makeActive" hidden data-i18n-html="editor.makeActive"></button>
  </div>
  <div class="buttons">
    <button type="button" id="ed-newBlank" data-i18n-html="editor.newBlank"></button>
    <button type="button" id="ed-export" data-i18n-html="editor.export"></button>
    <button type="button" id="ed-import" data-i18n-html="editor.import"></button>
    <input type="file" id="ed-importFile" accept=".json,application/json" hidden>
  </div>

  <h2 id="ed-mineTitle"></h2>
  <div class="row"><label for="ed-open" data-i18n-html="editor.mapLabel"></label><div class="pair"><select id="ed-open"></select><button type="button" id="ed-load" data-i18n-html="editor.open"></button></div></div>
  <div class="buttons" id="ed-mineButtons">
    <button type="button" id="ed-rename" data-i18n-html="editor.rename"></button>
    <button type="button" id="ed-remove" data-i18n-html="editor.remove"></button>
  </div>
  <div id="ed-templatesBox">
    <h2 data-i18n-html="editor.builtin"></h2>
    <p class="note" data-i18n-html="editor.builtinNote"></p>
    <div class="row"><label for="ed-template" data-i18n-html="editor.mapLabel"></label><div class="pair"><select id="ed-template"></select><button type="button" id="ed-loadTemplate" data-i18n-html="editor.open"></button></div></div>
  </div>

  <h2 data-i18n-html="editor.selection"></h2>
  <div id="ed-inspector"></div>

  <h2 data-i18n-html="editor.viewTitle"></h2>
  <div class="checks">
    <label><input type="checkbox" id="ed-snap"> <span data-i18n-html="editor.snap"></span> <input id="ed-snapStep" type="number" value="0.5" min="0.1" step="0.1" style="width:64px"> <span data-i18n-html="editor.metres"></span></label>
    <label><input type="checkbox" id="ed-ovIssues" checked> <span data-i18n-html="editor.ovIssues"></span></label>
    <label><input type="checkbox" id="ed-ovNav"> <span data-i18n-html="editor.ovNav"></span></label>
    <label><input type="checkbox" id="ed-ovSight"> <span data-i18n-html="editor.ovSight"></span></label>
  </div>

  <h2 data-i18n-html="editor.problems"></h2>
  <ul id="ed-issues"></ul>
</aside>
<footer id="ed-status"></footer>
`;
